/**
 * Work Time Toggle Component
 *
 * @module modules/workTime/presentation/components/WorkTimeToggle
 *
 * Button component for toggling work time status.
 * Displays different buttons based on current work state.
 */

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import { memo } from 'react';

import { useWorkTime } from '../../application/hooks';

import type { WorkTimeType } from '../../domain/types';

// ============================================================================
// Types
// ============================================================================

interface WorkTimeToggleProps {
  /** Whether the component is in a collapsed/compact mode */
  collapsed?: boolean;
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get display label for work time status
 */
function getStatusLabel(status: WorkTimeType): string {
  switch (status) {
    case 'online':
      return 'Online';
    case 'working':
      return 'Working';
    case 'paused':
      return 'Paused';
    case 'offline':
      return 'Offline';
    default:
      return 'Unknown';
  }
}

/**
 * Get color for work time status
 */
function getStatusColor(status: WorkTimeType): string {
  switch (status) {
    case 'online':
      return 'success.main';
    case 'working':
      return 'primary.main';
    case 'paused':
      return 'warning.main';
    case 'offline':
    default:
      return 'text.disabled';
  }
}

// ============================================================================
// Component
// ============================================================================

/**
 * Work Time Toggle Component
 *
 * Displays buttons based on current work time status:
 * - `online`: Shows "Start" button
 * - `working`: Shows "Pause" and "Stop" buttons
 * - `pause`: Shows "Resume" and "Stop" buttons
 * - `offline`: Shows disabled "Offline" indicator
 *
 * @example
 * ```tsx
 * <WorkTimeToggle />
 * ```
 */
export const WorkTimeToggle = memo(function WorkTimeToggle({
  collapsed = false,
}: WorkTimeToggleProps) {
  const { workTimeType, isLoading, startWorking, pauseWork, resumeWork, stopWork } = useWorkTime();

  // Loading state
  if (isLoading && !workTimeType) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 1 }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  // Status indicator dot
  const StatusDot = (
    <Box
      sx={{
        width: 8,
        height: 8,
        borderRadius: '50%',
        bgcolor: getStatusColor(workTimeType),
        flexShrink: 0,
      }}
    />
  );

  // Collapsed mode - just show status indicator
  if (collapsed) {
    return (
      <Tooltip title={getStatusLabel(workTimeType)} placement="right">
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            p: 1,
          }}
        >
          {StatusDot}
        </Box>
      </Tooltip>
    );
  }

  return (
    <Box sx={{ p: 1, flexShrink: 0 }}>
      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        {/* Online State - Show Start button */}
        {workTimeType === 'online' && (
          <Button
            variant="contained"
            color="primary"
            size="small"
            fullWidth
            startIcon={<PlayArrowIcon />}
            onClick={startWorking}
            disabled={isLoading}
            sx={{ borderRadius: 1 }}
          >
            Start
          </Button>
        )}

        {/* Working State - Show Pause and Stop buttons */}
        {workTimeType === 'working' && (
          <>
            <Button
              variant="contained"
              color="warning"
              size="small"
              startIcon={<PauseIcon />}
              onClick={pauseWork}
              disabled={isLoading}
              sx={{ borderRadius: 1, flex: 1 }}
            >
              Pause
            </Button>
            <Button
              variant="outlined"
              color="error"
              size="small"
              startIcon={<StopIcon />}
              onClick={stopWork}
              disabled={isLoading}
              sx={{ borderRadius: 1, flex: 1 }}
            >
              Stop
            </Button>
          </>
        )}

        {/* Pause State - Show Resume and Stop buttons */}
        {workTimeType === 'paused' && (
          <>
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={<PlayArrowIcon />}
              onClick={resumeWork}
              disabled={isLoading}
              sx={{ borderRadius: 1, flex: 1 }}
            >
              Resume
            </Button>
            <Button
              variant="outlined"
              color="error"
              size="small"
              startIcon={<StopIcon />}
              onClick={stopWork}
              disabled={isLoading}
              sx={{ borderRadius: 1, flex: 1 }}
            >
              Stop
            </Button>
          </>
        )}

        {/* Offline State - Show disabled indicator */}
        {workTimeType === 'offline' && (
          <Button
            variant="outlined"
            color="inherit"
            size="small"
            fullWidth
            startIcon={<WifiOffIcon />}
            disabled
            sx={{ borderRadius: 1, color: 'text.disabled' }}
          >
            Offline
          </Button>
        )}
      </Box>
    </Box>
  );
});
